import type {
  ConditionalActionConditionSubjectAnnotation,
  EnumOptionsDataSourceMapping,
  ConditionalActionValueType,
} from '@web-config-app/core';

/**
 * Re-exports of the framework's schema-annotation types. The control
 * consumes the core declarations directly — no parallel local shapes —
 * so the schema contract and the control's runtime types can't drift.
 */
export type ValueCatalogMapping = EnumOptionsDataSourceMapping;
export type ConditionalActionConditionSubject =
  ConditionalActionConditionSubjectAnnotation;

/**
 * Right-hand-side inputs an operator collects from the author. Set-membership
 * operators (`includes` / `not_includes`) take a set of codes under `values`;
 * numeric comparison operators (`>`, `>=`, `<`, `<=`) take a single `value`.
 * The set form is normalized to a set by the evaluator; a `dataPoint` numeric
 * condition's single `value` is evaluated server-side.
 */
export type OperatorInputs = { value?: string } | { values: string[] };

/**
 * One condition inside a conditional-action rule. Conditions inside a rule are
 * implicitly AND-joined; there's no combinator field.
 *
 * `subjectType` names the subject variant this condition targets — matches
 * the `type` on one of the conditional-action control's `conditionSubjects`
 * declared in the schema's `x-entity-control.options.conditionalAction`.
 * `operatorId` resolves against `CONDITIONAL_ACTION_OPERATORS`. `inputs` is
 * operator-specific.
 */
export interface ConditionalActionCondition {
  subjectType: string;
  /**
   * The picked subject entry's identifier — a question `linkId` for
   * `dataSource` subjects, a data-point id for `dataPoint` subjects. The
   * subject annotation's `valueCatalog` / `valueTypeFrom` then resolve
   * against that referenced item. Absent for `currentArrayItem` subjects (the
   * subject IS the enclosing item) and until the author picks an entry.
   */
  ref?: string;
  /** Empty/undefined when the author has selected a subject but not yet picked an operator. */
  operatorId?: string;
  inputs: OperatorInputs | Record<string, never>;
}

/**
 * A typed action — the consequence of a conditional-action rule (`then`) or the
 * top-level `default`. Modeled on Masonry actions: a `type` discriminator
 * plus a type-specific `payload`. v1 ships only `goTo` (navigate to a
 * question): `{ type: 'goTo', payload: { linkId } }`. New action types
 * (e.g. `fire`) are a new `type` + a renderer/evaluator case — no wire
 * change.
 */
export interface ConditionalActionAction {
  type: string;
  payload?: Record<string, unknown>;
}

/**
 * A conditional-action rule. Multiple conditions are implicitly AND-joined.
 * `then` is the action taken when the rule matches; absent or incomplete
 * = the rule is treated as non-matching (the runtime falls through).
 */
export interface ConditionalActionRule {
  conditions: ConditionalActionCondition[];
  then?: ConditionalActionAction;
}

export interface ConditionalActionData {
  rules?: ConditionalActionRule[];
  /** Action taken when no rule matches. Absent = linear next-in-order. */
  default?: ConditionalActionAction;
}

/** Build a `goTo` action for a destination linkId. */
export const goToAction = (linkId: string): ConditionalActionAction => ({
  type: 'goTo',
  payload: { linkId },
});

/**
 * Build a `show` action — the visibility configuration's per-rule effect.
 * Payload-less: a matched rule shows the question; when no rule matches the
 * implicit default is to hide it. The uniform action is why visibility
 * rules are effectively unordered (first-match-wins degenerates to "show
 * iff any rule matches").
 */
export const showAction = (): ConditionalActionAction => ({ type: 'show' });

/**
 * Read the destination linkId from a `goTo` action, or `undefined` when
 * the action is absent, a different type, or missing its `linkId`.
 */
export const getGoToLinkId = (
  action: ConditionalActionAction | undefined,
): string | undefined => {
  // `!action` also guards a stray runtime `null` (stored data is untrusted),
  // even though the declared type is `undefined`-only.
  if (!action || action.type !== 'goTo') return undefined;
  const linkId = action.payload?.linkId;
  return typeof linkId === 'string' ? linkId : undefined;
};

export interface ConditionalActionOperator {
  id: string;
  /** Locale key for the operator's display label. */
  label: string;
  /**
   * Logical value types this operator supports. Strict — empty means no
   * operator is applicable.
   */
  types: ConditionalActionValueType[];
  /**
   * Whether this operator needs a fixed list of values to choose from. The
   * set operators ("is any of" / "is none of") pick from such a list, so they
   * only apply when the subject actually has one — its value options, e.g. a
   * choice question's answer options. Numeric operators type in a value, so
   * they leave this false. (`applicableOperators` receives `hasValueOptions`:
   * whether that list resolves for the item being edited.)
   */
  requiresValueCatalog: boolean;
  /**
   * Shape of the value input the operator collects: `set` renders the
   * multi-value chip picker (`{ values }`); `scalar` renders a single value
   * input (`{ value }`), used by the numeric comparison operators.
   */
  inputShape: 'set' | 'scalar';
  /** Default input values when the operator is first selected. */
  defaultInputs: () => OperatorInputs;
}
